/**
 * Domain configuration for multi-site deployment.
 * 
 * In production, each domain is a separate Render static site build.
 * Locally, everything runs on localhost:3000 and cross-domain links become local paths.
 */

const SITE = process.env.REACT_APP_SITE || 'all'; // 'kandidaat' | 'bedrijven' | 'werepresent' | 'all' (dev)

const IS_PRODUCTION = SITE !== 'all';

// Domain URLs — in dev, alles is relatief (zelfde host)
const DOMAINS = IS_PRODUCTION
  ? {
      kandidaat: 'https://betaaldsolliciteren.nl',
      bedrijven: 'https://werepresenttalent.nl',
      werepresent: 'https://werepresent.nl',
    }
  : {
      kandidaat: '',
      bedrijven: '',
      werepresent: '',
    };

// Route paths per domain
const ROUTES = {
  // betaaldsolliciteren.nl
  kandidaatPortaal: IS_PRODUCTION ? '/mijn-profiel' : '/talent',
  
  // werepresenttalent.nl
  bedrijvenPortaal: IS_PRODUCTION ? '/organisatie' : '/business',
  bedrijvenLanding: IS_PRODUCTION ? '/' : '/bedrijven',
  
  // werepresent.nl
  werepresentLanding: IS_PRODUCTION ? '/' : '/werepresent',
  privacy: '/privacy',
  voorwaarden: '/voorwaarden',
  aiPolicy: '/ai-policy',
  cookieverklaring: '/cookieverklaring',
  unsubscribe: '/unsubscribe',
  admin: '/admin',
};

function getStoredLanguagePreference() {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem('bs_language');
    if (stored) return stored;
  } catch (_) {
    // ignore localStorage access errors
  }
  if (typeof document !== 'undefined') {
    return document.documentElement.lang || null;
  }
  return null;
}

function withLanguagePreference(url) {
  const lang = getStoredLanguagePreference();
  if (!lang) return url;

  if (!IS_PRODUCTION || !url.startsWith('http')) {
    const [path, hash = ''] = url.split('#');
    const [pathname, query = ''] = path.split('?');
    const params = new URLSearchParams(query);
    if (!params.has('lang')) {
      params.set('lang', lang);
    }
    const queryString = params.toString();
    return `${pathname}${queryString ? `?${queryString}` : ''}${hash ? `#${hash}` : ''}`;
  }

  try {
    const parsed = new URL(url);
    if (!parsed.searchParams.has('lang')) {
      parsed.searchParams.set('lang', lang);
    }
    return parsed.toString();
  } catch (_) {
    return url;
  }
}

/**
 * Genereer een URL die werkt in zowel dev als productie.
 * In dev: retourneert een lokaal pad (bv. "/bedrijven")
 * In productie: retourneert een volledige URL (bv. "https://werepresenttalent.nl/")
 * 
 * @param {string} domain - 'kandidaat' | 'bedrijven' | 'werepresent'
 * @param {string} path - Het pad op dat domein (bv. "/privacy")
 */
function domainUrl(domain, path = '/') {
  const base = DOMAINS[domain];
  if (!IS_PRODUCTION) {
    // Lokaal: gebruik de dev-route mapping
    return withLanguagePreference(path);
  }
  // Productie: volledige URL
  return withLanguagePreference(`${base}${path === '/' ? '' : path}`);
}

/**
 * Navigeer naar een URL. In dev gebruikt dit de SPA router,
 * in productie doet het een harde redirect bij cross-domain navigatie.
 * 
 * @param {string} url - De URL om naartoe te navigeren
 * @param {function} spaNavigate - De SPA navigate functie (optioneel)
 */
function navigateTo(url, spaNavigate) {
  const localizedUrl = withLanguagePreference(url);
  if (!IS_PRODUCTION || !url.startsWith('http')) {
    // Lokaal of same-domain: gebruik SPA navigatie
    if (spaNavigate) {
      spaNavigate(localizedUrl);
    } else {
      window.location.href = localizedUrl;
    }
  } else {
    // Cross-domain: harde redirect
    window.location.href = localizedUrl;
  }
}

export { SITE, IS_PRODUCTION, DOMAINS, ROUTES, domainUrl, navigateTo, withLanguagePreference };
